import { Resource as Resource$1, Literal as Literal$1, member } from '@ontologies/rdfs';
import React from 'react';
import rdf, { TermType, isNamedNode, doc, createNS, QuadPosition, isLiteral, isNode, isTerm, isBlankNode } from '@ontologies/core';
import { predicate, nil, first, rest, type, Seq, Alt, Bag, List, langString } from '@ontologies/rdf';
import { getTermBestLang, RENDER_CLASS_NAME, DEFAULT_TOPOLOGY, normalizeType, equals, RecordState, getPropBestLangRaw, id, LinkedRenderStore } from 'link-lib';
import { BAD_REQUEST } from 'http-status-codes';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { positiveInteger, nonNegativeInteger, negativeInteger, nonPositiveInteger, xsdshort, unsignedShort, xsdbyte, unsignedByte, xsdfloat, decimal, xsdboolean, dateTime, date, integer, xsdlong, xsdint, unsignedInt, unsignedLong, base64Binary, string } from '@ontologies/xsd';
import { decode } from 'base64-arraybuffer';
import PropTypes from 'prop-types';

class Component extends React.Component {
}
Component.type = Resource$1;

const LinkedRenderStoreCtx = React.createContext(undefined);
const LRSCtx = LinkedRenderStoreCtx;

/** Get the nearest LRS. */
function useLRS() {
    return React.useContext(LRSCtx);
}

function createPropertyRenderer(props, objRaw, lrs) {
    return (func, associationRenderer) => {
        const associationProps = associationRenderer !== React.Fragment ? props : null;
        if (objRaw.length === 0) {
            return null;
        }
        else if (objRaw.length === 1) {
            return React.createElement(associationRenderer, associationProps, func(objRaw[0]));
        }
        return React.createElement(associationRenderer, associationProps, func(getTermBestLang(objRaw, lrs.store.langPrefs)));
    };
}

function getLinkedObjectClass({ label, subject, topology, topologyCtx }, lrs, labelOverride) {
    var _a;
    return (_a = lrs.resourcePropertyComponent(subject, labelOverride || label, topology === null ? undefined : topology || topologyCtx)) !== null && _a !== void 0 ? _a : undefined;
}

function renderChildrenOrValue(props, lrs) {
    return function (p) {
        if (props.children || p.termType !== TermType.Literal) {
            return React.createElement(React.Fragment, null, props.children || p.value);
        }
        const { topology, topologyCtx, subjectCtx } = props;
        const t = topology === null ? undefined : topology || topologyCtx;
        const literalRenderer = lrs.getComponentForProperty(Literal$1, rdf.namedNode(p.datatype.value), t)
            || lrs.getComponentForProperty(Literal$1, RENDER_CLASS_NAME, t);
        if (!literalRenderer) {
            return React.createElement(React.Fragment, null, p.value);
        }
        return React.createElement(literalRenderer, {
            linkedProp: p,
            subject: p.datatype,
            subjectCtx,
            topology,
            topologyCtx,
        });
    };
}

function useCalculateChildProps(props, context, options = {}) {
    const lrs = useLRS();
    const { subject, topology } = context;
    const overrides = {};
    const reloadLinkedObject = React.useCallback(() => {
        const toReload = props.subject || subject;
        if (isNamedNode(toReload)) {
            lrs.queueEntity(toReload, { reload: true });
        }
        return Promise.resolve();
    }, [props.subject || subject, lrs]);
    if (typeof props.innerRef !== "undefined") {
        overrides.ref = props.innerRef;
        overrides.innerRef = undefined;
    }
    if (options.subject) {
        overrides.subjectCtx = subject;
        overrides.subject = props.subject;
    }
    if (options.topology) {
        overrides.topologyCtx = topology;
        overrides.topology = props.topology === null ? DEFAULT_TOPOLOGY : props.topology;
    }
    if (options.lrs) {
        overrides.lrs = lrs;
    }
    if (options.helpers) {
        overrides.reset = options.helpers.reset;
        overrides.reloadLinkedObject = reloadLinkedObject;
    }
    return Object.assign({}, props, { subject, topology: topology }, overrides);
}

const NO_SUBJECTS = [];
/**
 * The subjects this component subscribes to.
 * Includes the subject by default.
 */
function normalizeDataSubjects(props) {
    var _a;
    if (!(props.subject || props.dataSubjects)) {
        return NO_SUBJECTS;
    }
    const result = [];
    if (props.subject) {
        result.push(props.subject);
    }
    if (props.dataSubjects) {
        result.push(...normalizeType(props.dataSubjects));
    }
    if (((_a = props.subject) === null || _a === void 0 ? void 0 : _a.termType) === TermType.NamedNode) {
        const document = doc(props.subject);
        if (document !== props.subject.value) {
            result.push(rdf.namedNode(document));
        }
    }
    if (result.length === 0) {
        return NO_SUBJECTS;
    }
    if (result.length === 1) {
        return result[0];
    }
    return result;
}
/**
 * Re-renders when one of the given {subjects} changes in the store.
 *
 * Should only be necessary when using imperative code.
 */
function useDataInvalidation(subjects) {
    const lrs = useLRS();
    const invalidations = () => normalizeType(subjects).filter(Boolean).map((n) => n.value);
    const calculateString = () => JSON.stringify(invalidations().map((id) => lrs.getState(id).lastUpdate));
    const [, setValue] = React.useState(calculateString);
    const ids = normalizeType(subjects).filter(Boolean).map((n) => n.value);
    const idsDep = JSON.stringify(ids);
    React.useEffect(() => {
        return lrs.subscribe({
            callback: () => setValue(calculateString()),
            lastUpdateAt: undefined,
            markedForDelete: false,
            subjectFilter: ids,
        });
    }, [
        lrs,
        idsDep,
    ]);
    return calculateString();
}

const LinkRenderCtx = React.createContext({
    subject: undefined,
    topology: DEFAULT_TOPOLOGY,
});

/**
 * @internal `useTopology` or `useSubject` instead.
 */
const useLinkRenderContext = () => React.useContext(LinkRenderCtx);

const ll = createNS("http://purl.org/link-lib/");
var ll$1 = {
    ns: ll,
    /* classes */
    ErrorResource: ll("ErrorResource"),
    LoadingResource: ll("LoadingResource"),
    NoView: ll("NoView"),
    /* properties */
    clonedFrom: ll("clonedFrom"),
    /** Used internally by link to ensure data consistency */
    dataSubject: ll("dataSubject"),
    meta: ll("meta"),
    /* individuals */
    /** No-operation, used internally by link when information is not needed. */
    nop: ll("nop"),
};

function nextRenderContext(props) {
    return {
        subject: props.subject,
        topology: props.topology || props.topologyCtx,
    };
}
function wrapRenderContext(props, comp) {
    return React.createElement(LinkRenderCtx.Provider, { value: nextRenderContext(props) }, comp);
}
function renderNoView(props, lrs) {
    const NoView = lrs.getComponentForProperty(ll$1.NoView, (props.label || RENDER_CLASS_NAME), props.topology || props.topologyCtx);
    const message = `No view found for subject '${props.subject}' label '${props.label}' and topology ${props.topology || props.topologyCtx}`;
    lrs.report(new Error(message));
    if (NoView) {
        return React.createElement(NoView);
    }
    return React.createElement("div", { "className": "no-view", "data-testid": "no-view" }, React.createElement("p", null, message));
}
function renderError(props, lrs, error) {
    const errComp = errorComponent(props, lrs);
    if (errComp) {
        return wrapRenderContext(props, React.createElement(errComp, Object.assign({}, props, {
            error: error instanceof Error ? error : undefined,
            linkRequestStatus: lrs.getStatus(props.subject || props.subjectCtx),
            report: lrs.report,
            subject: props.subject,
        })));
    }
    return null;
}
function errorComponent(props, lrs) {
    return props.onError
        || lrs.getComponentForType(ll$1.ErrorResource, props.topology || props.topologyCtx)
        || null;
}
function loadingComponent(props, lrs) {
    return props.onLoad
        || lrs.getComponentForType(ll$1.LoadingResource, props.topology || props.topologyCtx)
        || null;
}

function useChildPropsOrFallback(props) {
    const options = { topology: true };
    const lrs = useLRS();
    const [error, setError] = React.useState(undefined);
    const context = useLinkRenderContext();
    const subjectData = lrs.tryRecord(context.subject);
    const childProps = useCalculateChildProps(props, context, options);
    try {
        useDataInvalidation(normalizeDataSubjects(childProps));
    }
    catch (e) {
        setError(e);
    }
    if (subjectData === undefined || childProps.label === undefined) {
        return null;
    }
    let values = [];
    if (Array.isArray(childProps.label)) {
        values = childProps.label.reduce((acc, l) => {
            const v = subjectData[l.value];
            if (Array.isArray(v)) {
                return [...acc, ...v];
            }
            else if (v !== undefined) {
                return [...acc, v];
            }
            else {
                return acc;
            }
        }, []);
    }
    else {
        const v = subjectData[childProps.label.value];
        if (Array.isArray(v)) {
            values = v;
        }
        else if (v !== undefined) {
            values = [v];
        }
    }
    if (error) {
        return renderError(childProps, lrs, error);
    }
    if (values.length === 0 && !childProps.forceRender) {
        return null;
    }
    return [childProps, values];
}

/**
 * Useful for using as a hook update dependency.
 */
function useMemoizedDataSubjects(subjects) {
    return JSON.stringify(subjects);
}
const calculatedValueChanged = (a, b) => (Array.isArray(a) && Array.isArray(b))
    ? a.length !== b.length || !a.every((t, i) => t === b[i])
    : !equals(a, b);

/**
 * Fetches the {props.subject} if it has no data or is expired.
 *
 * @param resources The resources to fetch.
 * @return The last update stamp, useful in a hook dependency list.
 *
 * @see {LinkedRenderStore#shouldLoadResource} for the triggering mechanism.
 */
function useDataFetching(resources) {
    const lrs = useLRS();
    const lastUpdate = useDataInvalidation(resources);
    React.useEffect(() => {
        const toCheck = normalizeType(resources);
        for (const resource of toCheck) {
            if (resource && lrs.shouldLoadResource(resource)) {
                lrs.queueEntity(resource);
            }
        }
    }, [
        lrs,
        useMemoizedDataSubjects(resources),
        lastUpdate,
    ]);
    return lastUpdate;
}

const LOADING_STATES = [
    RecordState.Queued,
    RecordState.Requested,
    RecordState.Receiving,
];
function useRenderLoadingOrError(props, error) {
    const lrs = useLRS();
    if (error) {
        return renderError(props, lrs, error);
    }
    const { current, previous } = lrs.getState(props.subject.value);
    const status = lrs.getStatus(props.subject);
    const hasError = status.status >= BAD_REQUEST;
    if (previous === RecordState.Present && current !== RecordState.Absent && !hasError) {
        return undefined;
    }
    if (current === RecordState.Absent || LOADING_STATES.includes(current)) {
        const loadComp = loadingComponent(props, lrs);
        return loadComp === null
            ? null
            : wrapRenderContext(props, React.createElement(loadComp, props));
    }
    if (hasError) {
        return renderError(props, lrs, error);
    }
    return undefined;
}

function useResourcePropertyView(subject, predicate = RENDER_CLASS_NAME, topology = DEFAULT_TOPOLOGY) {
    var _a;
    const lrs = useLRS();
    const context = useLinkRenderContext();
    return (_a = lrs.resourcePropertyComponent(subject || context.subject, predicate, topology || context.topology)) !== null && _a !== void 0 ? _a : undefined;
}

function useResourceView(subject, topology = DEFAULT_TOPOLOGY) {
    var _a;
    return (_a = useResourcePropertyView(subject, RENDER_CLASS_NAME, topology)) !== null && _a !== void 0 ? _a : undefined;
}

function useCalculatedViewWithState(props, lrs, error) {
    const notReadyComponent = useRenderLoadingOrError(props, error);
    const component = useResourceView(props.subject, props.topology || props.topologyCtx);
    if ((props.forceRender && props.children) || (notReadyComponent === undefined && props.children)) {
        return React.createElement(React.Fragment, null, props.children);
    }
    if (notReadyComponent !== undefined) {
        return notReadyComponent;
    }
    if (component !== undefined && component != null) {
        return React.createElement(component, props);
    }
    return renderNoView(props, lrs);
}
function LRC(props, _) {
    const context = useLinkRenderContext();
    const [error, setError] = React.useState(undefined);
    const reset = React.useCallback(() => setError(undefined), [setError]);
    const options = {
        helpers: {
            reset,
        },
        subject: true,
        topology: true,
    };
    const lrs = useLRS();
    const childProps = useCalculateChildProps(props, context, options);
    useDataFetching(normalizeDataSubjects(childProps));
    const comp = useCalculatedViewWithState(childProps, lrs, error);
    return wrapRenderContext(childProps, comp);
}
LRC.defaultProps = {
    children: undefined,
    forceRender: false,
    onError: undefined,
    onLoad: undefined,
    topology: undefined,
};
LRC.displayName = "Resource";
const Resource = React.forwardRef((props, ref) => React.createElement(LRC, { innerRef: ref, ...props }));

const nodeTypes = [TermType.NamedNode, TermType.BlankNode];
const Property = (props) => {
    const lrs = useLRS();
    const childPropsOrFallback = useChildPropsOrFallback(props);
    if (childPropsOrFallback === null || !Array.isArray(childPropsOrFallback)) {
        return childPropsOrFallback;
    }
    const [childProps, objRaw] = childPropsOrFallback;
    const associationRenderer = getLinkedObjectClass(childProps, lrs, predicate) || React.Fragment;
    const associationProps = associationRenderer !== React.Fragment ? childProps : null;
    const childComp = typeof childProps.children === "function"
        ? childProps.children(objRaw)
        : childProps.children;
    if (typeof childProps.children === "function") {
        return React.createElement(associationRenderer, associationProps, childComp);
    }
    const propertyRenderer = createPropertyRenderer(childProps, objRaw, lrs);
    const component = getLinkedObjectClass(childProps, lrs);
    if (component) {
        const toRender = propertyRenderer((p) => React.createElement(component, { ...childProps, linkedProp: p }, childComp), associationRenderer);
        if (toRender === null) {
            return React.createElement(associationRenderer, associationProps, React.createElement(component, { ...childProps }, childComp));
        }
        return toRender;
    }
    else if (objRaw.length > 0) {
        if (nodeTypes.includes(objRaw[0].termType)) {
            const wrapLOC = (p) => {
                const lrcProps = {
                    ...childProps,
                    subject: p,
                };
                return React.createElement(Resource, { ...lrcProps }, childComp);
            };
            return propertyRenderer(wrapLOC, associationRenderer);
        }
        return propertyRenderer(renderChildrenOrValue(childProps, lrs), associationRenderer);
    }
    if (childProps.children) {
        return React.createElement(associationRenderer, associationProps, childComp);
    }
    return null;
};
Property.defaultProps = {
    forceRender: false,
    limit: 1,
    linkedProp: undefined,
};
Property.displayName = "Property";

const RenderStoreProvider = ({ children, value }) => {
    return React.createElement(LRSCtx.Provider, { value }, children);
};

/**
 * Inherit from this component to set the topology.
 *
 * @example
 * ```
 *
 *   class Collection extends TopologyProvider {
 *     this.topology = NS.app('collection')
 *     // Optionally:
 *     this.className = "Collection";
 *     this.elementName = "span";
 *   }
 * ```
 */
class TopologyProvider extends React.Component {
    constructor() {
        super(...arguments);
        this.className = undefined;
        this.elementType = "div";
        this.topology = null;
    }
    wrap(children) {
        return React.createElement(LinkRenderCtx.Consumer, null, ({ subject }) => React.createElement(LinkRenderCtx.Provider, {
            value: {
                subject,
                topology: this.topology === null ? undefined : this.topology,
            },
        }, typeof children === "function" ? children(subject) : children));
    }
    render() {
        let children = this.props.children;
        if (this.className !== undefined) {
            children = React.createElement(this.elementType, { className: this.className, ...this.props }, this.props.children);
        }
        return this.wrap(children);
    }
}

const Type = React.forwardRef((props, ref) => {
    const lrs = useLRS();
    const context = useLinkRenderContext();
    const childProps = useCalculateChildProps(props, context);
    useDataInvalidation(normalizeDataSubjects(childProps));
    const notReadyComponent = useRenderLoadingOrError(childProps);
    const component = useResourcePropertyView(childProps.subject, (childProps.label || RENDER_CLASS_NAME), childProps.topology || childProps.topologyCtx);
    if (notReadyComponent !== undefined) {
        return notReadyComponent;
    }
    if (component !== undefined && component !== null) {
        const { children, ...rest // tslint:disable-line trailing-comma
         } = childProps;
        return React.createElement(component, { ...rest, innerRef: ref }, children);
    }
    return renderNoView(childProps, lrs);
});

/**
 * Data types to which the data can be converted before inserting into a data map
 * @deprecated
 */
var ReturnType;
(function (ReturnType) {
    /** Return the `object`, keeping the underlying rdf data model. */
    ReturnType[ReturnType["Term"] = 0] = "Term";
    /**
     * Keep the underlying rdf data model
     * @deprecated
     */
    ReturnType[ReturnType["Statement"] = 1] = "Statement";
    /** Return the `object` converted to the nearest matching JS type, or a plain string if not possible. */
    ReturnType[ReturnType["Literal"] = 2] = "Literal";
    /** Return the `object` as a string value. */
    ReturnType[ReturnType["Value"] = 3] = "Value";
    /** Return the `object`, keeping the underlying rdf data model. */
    ReturnType[ReturnType["AllTerms"] = 4] = "AllTerms";
    /**
     * Keep the underlying rdf data model
     * @deprecated
     */
    ReturnType[ReturnType["AllStatements"] = 5] = "AllStatements";
    /** Return the `object` converted to the nearest matching JS type, or a plain string if not possible. */
    ReturnType[ReturnType["AllLiterals"] = 6] = "AllLiterals";
    /** Return the `object` as a string value. */
    ReturnType[ReturnType["AllValues"] = 7] = "AllValues";
})(ReturnType || (ReturnType = {}));
const defaultPropertyOptions = {
    returnType: ReturnType.AllTerms,
};
const defaultLinkOptions = {
    returnType: ReturnType.Term,
};

const globalLinkOptsDefaults = {
    fetch: true,
    forceRender: false,
    limit: Infinity,
    returnType: ReturnType.Term,
};

function mapMultiLabelMap(propKey, predObj, opts) {
    if (predObj.length === 0) {
        throw new TypeError("Props array must contain at least one predicate");
    }
    return [
        predObj,
        {
            fetch: opts.fetch || globalLinkOptsDefaults.fetch,
            forceRender: opts.forceRender || globalLinkOptsDefaults.forceRender,
            label: predObj,
            limit: opts.limit || globalLinkOptsDefaults.limit,
            name: propKey,
            returnType: opts.returnType || globalLinkOptsDefaults.returnType,
        },
    ];
}
function mapLabelMap(propKey, predObj, opts) {
    return [
        [predObj],
        {
            fetch: opts.fetch || globalLinkOptsDefaults.fetch,
            forceRender: opts.forceRender || globalLinkOptsDefaults.forceRender,
            label: [predObj],
            limit: opts.limit || globalLinkOptsDefaults.limit,
            name: propKey,
            returnType: opts.returnType || globalLinkOptsDefaults.returnType,
        },
    ];
}
function mapLinkOptsMap(propKey, predObj, opts) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const labels = normalizeType(predObj.label).filter(Boolean);
    if (predObj.label === undefined) {
        throw new TypeError(`Prop with key '${propKey}' contains no valid labels`);
    }
    return [
        labels,
        {
            fetch: (_b = (_a = predObj.fetch) !== null && _a !== void 0 ? _a : opts.fetch) !== null && _b !== void 0 ? _b : globalLinkOptsDefaults.fetch,
            forceRender: (_d = (_c = predObj.forceRender) !== null && _c !== void 0 ? _c : opts.forceRender) !== null && _d !== void 0 ? _d : globalLinkOptsDefaults.forceRender,
            label: normalizeType(predObj.label),
            limit: (_f = (_e = predObj.limit) !== null && _e !== void 0 ? _e : opts.limit) !== null && _f !== void 0 ? _f : globalLinkOptsDefaults.limit,
            linkedProp: predObj.linkedProp || opts.linkedProp,
            name: propKey,
            returnType: (_h = (_g = predObj.returnType) !== null && _g !== void 0 ? _g : opts.returnType) !== null && _h !== void 0 ? _h : globalLinkOptsDefaults.returnType,
        },
    ];
}
function dataPropToPropMap(propKey, predObj, opts) {
    if (Array.isArray(predObj)) {
        return mapMultiLabelMap(propKey, predObj, opts);
    }
    else if (isNamedNode(predObj)) {
        return mapLabelMap(propKey, predObj, opts);
    }
    return mapLinkOptsMap(propKey, predObj, opts);
}
function dataPropsToPropMap(mapDataToProps, opts) {
    let requestedProperties = [];
    const propMap = Object
        .keys(mapDataToProps)
        .reduce((acc, propKey) => {
        const predObj = mapDataToProps[propKey];
        const [properties, mapping] = dataPropToPropMap(propKey, predObj, opts);
        if (mapping.name.trim().length === 0) {
            throw new TypeError("Pass a valid prop label");
        }
        requestedProperties = requestedProperties.concat(...properties);
        return {
            ...acc,
            [mapping.name]: mapping,
        };
    }, {});
    return [propMap, Array.from(new Set(requestedProperties))];
}

const integerTypes = [
    integer,
    xsdlong,
    xsdint,
    unsignedInt,
    unsignedLong,
];
const bigIntTypes = [
    positiveInteger,
    nonNegativeInteger,
    negativeInteger,
    nonPositiveInteger,
];
const numberTypes = [
    ...integerTypes,
    xsdshort,
    unsignedShort,
    xsdbyte,
    unsignedByte,
    xsdfloat,
    decimal,
];
// From rdflib.js with modifications
function toJS(obj) {
    if (!isLiteral(obj)) {
        return obj;
    }
    if (equals(obj.datatype, xsdboolean)) {
        return obj.value === "true" || obj.value === "1" || obj.value === "t";
    }
    if (equals(obj.datatype, dateTime) ||
        equals(obj.datatype, date)) {
        return new Date(obj.value);
    }
    if (bigIntTypes.some((type) => equals(obj.datatype, type))) {
        return BigInt(obj.value);
    }
    if (numberTypes.some((type) => equals(obj.datatype, type))) {
        return Number(obj.value);
    }
    return obj.value;
}
function toReturnType(returnType, p) {
    var _a, _b, _c;
    // @ts-ignore
    const stmts = normalizeType(p);
    switch (returnType) {
        case ReturnType.Literal:
            return toJS((_a = stmts[0]) === null || _a === void 0 ? void 0 : _a[QuadPosition.object]);
        case ReturnType.Value:
            return (_b = stmts[0]) === null || _b === void 0 ? void 0 : _b[QuadPosition.object].value;
        case ReturnType.Term:
            return (_c = stmts[0]) === null || _c === void 0 ? void 0 : _c[QuadPosition.object];
        case ReturnType.Statement:
            return stmts[0];
        case ReturnType.AllLiterals:
            return stmts.map((s) => toJS(s[QuadPosition.object]));
        case ReturnType.AllValues:
            return stmts.map((s) => s[QuadPosition.object].value);
        case ReturnType.AllTerms:
            return stmts.map((s) => s[QuadPosition.object]);
        case ReturnType.AllStatements:
            return stmts;
        default:
            throw new TypeError(`Unknown returnType '${returnType}' given`);
    }
}

const defaultGraph = rdf.defaultGraph();
function useManyLinkedObjectProperties(propSets, propMap, returnType) {
    const returnTypeOrDefault = returnType !== null && returnType !== void 0 ? returnType : globalLinkOptsDefaults.returnType;
    const lrs = useLRS();
    const values = React.useMemo(() => Object.values(propMap), [propMap]);
    return React.useMemo(() => {
        var _a, _b;
        const propMaps = [];
        for (const set of propSets) {
            const acc = {};
            if (set === undefined || set[0] === undefined) {
                acc.subject = toReturnType(returnTypeOrDefault, []);
                propMaps.push(acc);
                continue;
            }
            const [s, record] = set;
            const subject = s.includes(":") && !s.startsWith("_:")
                ? rdf.namedNode(s)
                : rdf.blankNode(s);
            acc.subject = toReturnType(returnTypeOrDefault, [[subject, ll$1.dataSubject, subject, rdf.defaultGraph()]]);
            for (let i = 0, ilen = values.length; i < ilen; i++) {
                const propOpts = values[i];
                for (let j = 0, jlen = propOpts.label.length; j < jlen; j++) {
                    const cur = propOpts.label[j];
                    if (acc[propOpts.name]) {
                        // TODO: Merge
                        continue;
                    }
                    const field = record[cur.value];
                    if (field === undefined) {
                        acc[propOpts.name] = toReturnType((_a = propOpts.returnType) !== null && _a !== void 0 ? _a : returnTypeOrDefault, []);
                        continue;
                    }
                    const data = normalizeType(field).map((value) => [
                        subject,
                        cur,
                        value,
                        defaultGraph,
                    ]);
                    const best = data.length === 1 ? data[0] : getPropBestLangRaw(data, lrs.store.langPrefs);
                    if (data[0] !== best) {
                        data[data.indexOf(best)] = data[0];
                        data[0] = best;
                    }
                    acc[propOpts.name] = toReturnType((_b = propOpts.returnType) !== null && _b !== void 0 ? _b : returnTypeOrDefault, data);
                }
            }
            propMaps.push(acc);
        }
        return propMaps;
    }, [lrs, propSets, values]);
}

function useLinkedObjectProperties(subjProps, propMap, returnType) {
    const [data] = useManyLinkedObjectProperties(subjProps, propMap, returnType);
    return data;
}

const createConnectedComponent = (propMap, opts, wrappedComponent) => React.forwardRef((props, ref) => {
    var _a;
    const lrs = useLRS();
    const context = useLinkRenderContext();
    const childProps = useCalculateChildProps({ ...props, innerRef: ref }, context, { lrs: true });
    const record = lrs.tryRecord(childProps.subject);
    const records = record === undefined ? [] : [[childProps.subject.value, record]];
    const fieldProps = useLinkedObjectProperties(records, propMap, (_a = opts.returnType) !== null && _a !== void 0 ? _a : ReturnType.Term);
    const mappedProps = {
        ...childProps,
        ...fieldProps,
        subject: childProps.subject,
    };
    const linkVersion = useDataInvalidation(normalizeDataSubjects(mappedProps));
    if ((props.forceRender || opts.forceRender) !== true && record === undefined) {
        return null;
    }
    return React.createElement(wrappedComponent, {
        ...mappedProps,
        linkVersion,
    });
});
const wrapWithConnect = (propMap, opts) => (wrappedComponent) => {
    const comp = createConnectedComponent(propMap, opts, wrappedComponent);
    comp.displayName = `linkMapDataToProps(${comp.displayName || comp.name})`;
    return hoistNonReactStatics(comp, wrappedComponent);
};

const wrapWithSubject = (WrappedComponent) => {
    const Comp = (props) => {
        const { subject } = useLinkRenderContext();
        const shared = { ...props, subject };
        return React.createElement(WrappedComponent, shared);
    };
    Comp.displayName = `linkSubject(${WrappedComponent.displayName || WrappedComponent.name})`;
    return Comp;
};

/**
 * Binds a react component to data properties.
 *
 * The current implementation only supports a one-dimensional array of NamedNode objects. And binds
 * the underlying values to the `props` object with each predicate term as the key.
 *
 * @example
 * ```
 *
 *   const BlogPost = (props) => (
 *     <div>
 *         <h1>{props.name.value}</h1>
 *         <p>{props.text.value}</p>
 *         <Resource subject={props.author} />
 *     </div
 *   )
 *
 *   link([NS.schema("name"), NS.schema("text"), NS.schema("author")])(BlogPost)
 * ```
 * @param {NamedNode[]} mapDataToProps The properties to bind to the component, only NamedNode[] is currently supported.
 * @param {LinkOpts} opts Adjust the default behaviour, these are not yet guaranteed.
 * @deprecated See {useLink}
 */
function link(mapDataToProps = {}, opts = globalLinkOptsDefaults) {
    const [propMap, requestedProperties] = dataPropsToPropMap(mapDataToProps, opts);
    if (requestedProperties.length === 0) {
        if (opts !== globalLinkOptsDefaults) {
            throw new TypeError("Bind at least one prop to use render opts");
        }
        return wrapWithSubject;
    }
    return wrapWithConnect(propMap, opts);
}

/** @deprecated Use `useLRS` instead. */
function withLRS(Component) {
    const Comp = (props) => {
        const lrs = useLRS();
        return React.createElement(Component, { ...props, lrs: lrs });
    };
    return hoistNonReactStatics(Comp, Component);
}

/** @deprecated Use `useTopology` instead. */
function withTopology(Component) {
    return (props) => (React.createElement(LinkRenderCtx.Consumer, null, ({ topology }) => {
        const merged = { ...props, topology };
        return React.createElement(Component, { ...merged });
    }));
}

/**
 * Runs the applicable {calculator} only once while also giving the result on the first render.
 */
const useCalculatedValue = (calculator, invalidations, subjects, ...args) => {
    const lrs = useLRS();
    const invalidation = useDataFetching(subjects);
    return React.useMemo(() => calculator(lrs, subjects, ...args)[0], [
        lrs,
        invalidation,
        subjects,
        ...invalidations,
    ]);
};

const EMPTY_ARRAY = [];
const NESTED_EMPTY_ARRAY = [[], []];

const base = "http://www.w3.org/1999/02/22-rdf-syntax-ns#_";
function numAsc(a, b) {
    const aP = Number.parseInt(a[QuadPosition.predicate].value.slice(base.length), 10);
    const bP = Number.parseInt(b[QuadPosition.predicate].value.slice(base.length), 10);
    return aP - bP;
}
function orderedElementsOfSeq(store, seqIRI) {
    const elements = store
        .getResourcePropertyRaw(seqIRI, member)
        .sort(numAsc);
    return [elements, [seqIRI]];
}
function orderedElementsOfList(store, listEntry) {
    const list = [];
    const nodes = [listEntry];
    let next = listEntry;
    while (next && next !== nil) {
        const item = store.getResourcePropertyRaw(next, first)[0];
        if (!item) {
            break;
        }
        list.push(item);
        const rest$1 = store.getResourceProperty(next, rest);
        if (!rest$1 || isLiteral(rest$1) || nodes.includes(rest$1)) {
            break;
        }
        nodes.push(next);
        next = rest$1;
    }
    return [list, nodes];
}

var QueryType;
(function (QueryType) {
    QueryType[QueryType["Except"] = 0] = "Except";
    QueryType[QueryType["Dig"] = 1] = "Dig";
    QueryType[QueryType["Array"] = 2] = "Array";
})(QueryType || (QueryType = {}));

const defaultGraph$1 = rdf.defaultGraph();
const isFieldQuery = (v) => Array.isArray(v) || isNamedNode(v);
const undefinedResolver = () => () => NESTED_EMPTY_ARRAY;
const simpleResolver = (lrs, query) => (s) => [lrs.getResourcePropertyRaw(s, query), EMPTY_ARRAY];
const digResolver = (lrs, query) => (target) => lrs.digDeeper(target, query.path);
const exceptResolver = (lrs, query) => (s) => {
    if (s === undefined) {
        return NESTED_EMPTY_ARRAY;
    }
    const fields = query.fields.map((f) => f.value);
    const record = lrs.tryRecord(s);
    if (record === undefined) {
        return [[], EMPTY_ARRAY];
    }
    const quads = [];
    // tslint:disable-next-line:forin
    for (const f in record) {
        const field = rdf.namedNode(f);
        if (!record.hasOwnProperty(f) || f === "_id" || fields.includes(f)) {
            continue;
        }
        const values = record[f];
        if (Array.isArray(values)) {
            for (const v of values) {
                quads.push([s, field, v, defaultGraph$1]);
            }
        }
        else {
            quads.push([s, field, values, defaultGraph$1]);
        }
    }
    return [
        quads,
        EMPTY_ARRAY,
    ];
};
const arrayResolver = (lrs, query) => (target) => {
    if (target === undefined) {
        return NESTED_EMPTY_ARRAY;
    }
    const targets = lrs.getResourceProperties(target, query.fields);
    for (const t of targets) {
        if (!isNode(t)) {
            continue;
        }
        const types = lrs.getResourceProperties(t, type);
        if (types.some((type) => equals(type, Seq) || equals(type, Alt) || equals(type, Bag))) {
            return orderedElementsOfSeq(lrs, t);
        }
        if (types.some((type) => equals(type, List))) {
            return orderedElementsOfList(lrs, t);
        }
    }
    return NESTED_EMPTY_ARRAY;
};
const resolver = (lrs, query) => {
    if (query === undefined) {
        return undefinedResolver();
    }
    else if (isFieldQuery(query)) {
        return simpleResolver(lrs, query);
    }
    else if (query.type === QueryType.Dig) {
        return digResolver(lrs, query);
    }
    else if (query.type === QueryType.Except) {
        return exceptResolver(lrs, query);
    }
    else if (query.type === QueryType.Array) {
        return arrayResolver(lrs, query);
    }
    throw new Error(`unknown resolver type (${query})`);
};

const queryChanged = (previous, next) => {
    if (previous === next) {
        return false;
    }
    if (previous === undefined || next === undefined) {
        return previous !== next;
    }
    if (Array.isArray(previous) && Array.isArray(next)) {
        return previous.length !== next.length
            || previous.some((p, i) => !equals(p, next[i]));
    }
    if (Array.isArray(previous) || Array.isArray(next)) {
        return true;
    }
    if (isTerm(previous) || isTerm(next)) {
        return !equals(previous, next);
    }
    if (previous.type !== next.type) {
        return true;
    }
    switch (previous.type) {
        case QueryType.Dig:
            return previous.path.length !== next.path.length
                || previous.path.some((p, i) => {
                    const nextPath = next.path[i];
                    if (isTerm(p)) {
                        return !equals(p, nextPath);
                    }
                    else {
                        return Array.isArray(nextPath)
                            ? p.some((path, ip) => !equals(path, nextPath[ip]))
                            : true;
                    }
                });
        case QueryType.Array:
        case QueryType.Except:
            return previous.fields.length !== next.fields.length
                || previous.fields.some((p, i) => !equals(p, next.fields[i]));
    }
};

const targetsChanged = (previous, next) => {
    if (previous === next) {
        return false;
    }
    if (previous === undefined || next === undefined) {
        return previous !== next;
    }
    if (Array.isArray(previous) && Array.isArray(next)) {
        return previous.length !== next.length
            || previous.some((p, i) => !equals(p, next[i]));
    }
    if (Array.isArray(previous) || Array.isArray(next)) {
        return true;
    }
    return !equals(previous, next);
};

const calculate = (subject, resource, fields) => {
    if (fields === null) {
        return [subject, resource];
    }
    const multipleFields = Array.isArray(fields);
    const multipleResources = Array.isArray(resource);
    if (fields === undefined || resource === undefined) {
        return [undefined, undefined];
    }
    else if (multipleFields && fields.length === 0 || multipleResources && resource.length === 0) {
        return [undefined, undefined];
    }
    return [resource, fields];
};
const useTargetedQuery = (resource, query) => {
    const { subject } = useLinkRenderContext();
    const targetsRef = React.useRef(undefined);
    const fieldsRef = React.useRef(undefined);
    return React.useMemo(() => {
        const [nextTargets, nextFields] = calculate(subject, resource, query);
        if (targetsChanged(targetsRef.current, nextTargets)) {
            targetsRef.current = nextTargets;
        }
        if (queryChanged(fieldsRef.current, nextFields)) {
            fieldsRef.current = nextFields;
        }
        return [targetsRef.current, fieldsRef.current];
    }, [subject, resource, query]);
};

const calculate$1 = (lrs, subject, parser, query) => {
    if (!subject) {
        return NESTED_EMPTY_ARRAY;
    }
    const boundParser = parser(lrs);
    const mapper = resolver(lrs, query);
    const calc = (s) => {
        const [values, targets] = mapper(s);
        const mapped = values
            .map(boundParser)
            .filter((it) => typeof it !== "undefined");
        return [mapped, targets];
    };
    if (Array.isArray(subject)) {
        const values = [];
        for (let i = 0, iLen = subject.length; i < iLen; i++) {
            const [t, targets] = calc(subject[i]);
            values.push(t);
        }
        return [values, []];
    }
    return calc(subject);
};
const makeParsedField = (parser) => {
    const dataHook = (resource, field = null) => {
        const [targets, query] = useTargetedQuery(resource, field);
        const invalidation = useDataFetching(targets);
        return useCalculatedValue(calculate$1, [query, invalidation], targets, parser, query);
    };
    return dataHook;
};

/**
 * Will resolve all fields except those passed.
 */
const except = (...fields) => ({
    fields,
    type: QueryType.Except,
});
/**
 * Will resolve fields at the end of {path}, traversing intermediate records.
 */
const dig = (...path) => ({
    path,
    type: QueryType.Dig,
});
/**
 * Experimental. Will unpack {fields} if their value refers to rdf:Seq, rdf:List, rdf:Container, rdf:Bag.
 * Note that their values will be returned top-level, rather than nested.
 * Has undefined behaviour with regards to multimap values.
 */
const array = (...fields) => ({
    fields,
    type: QueryType.Array,
});
/**
 * Retrieves fields of any type as raw terms.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useQuadruples = makeParsedField((_) => (it) => it);
/**
 * Retrieves all fields as internal representation.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useFields = makeParsedField((_) => (it) => it[QuadPosition.object]);
/**
 * Retrieves fields which contain association.
 * @param {fields} {Node} - The field to retrieve from the record.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useIds = makeParsedField((_) => (it) => isNode(it[QuadPosition.object])
    ? it[QuadPosition.object]
    : undefined);
/**
 * Retrieves fields which contain namedNode.
 * @param {fields} {Node} - The field to retrieve from the record.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useGlobalIds = makeParsedField((_) => (it) => isNamedNode(it[QuadPosition.object])
    ? it[QuadPosition.object]
    : undefined);
// TODO: Different hook which filters on on- and off-site?
/**
 * Retrieves fields which contain blankNode.
 * @param {fields} {Node} - The field to retrieve from the record.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useLocalIds = makeParsedField((_) => (it) => isBlankNode(it[QuadPosition.object])
    ? it[QuadPosition.object]
    : undefined);
/**
 * Retrieves fields which contain literal.
 * @param {fields} {Node} - The field to retrieve from the record.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useLiterals = makeParsedField((_) => (it) => isLiteral(it[QuadPosition.object])
    ? it[QuadPosition.object]
    : undefined);
/**
 * Retrieves all value {fields}.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useValues = makeParsedField((_) => (it) => it[QuadPosition.object].value);
/**
 * Retrieves all literalValue {fields}.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useLiteralValues = makeParsedField((_) => (it) => isLiteral(it[QuadPosition.object])
    ? it[QuadPosition.object].value
    : undefined);
/**
 * Retrieves all base64 {fields}.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useBase64s = makeParsedField((_) => (quad) => {
    const it = quad[QuadPosition.object];
    if (!isLiteral(it) || !equals(it.datatype, base64Binary)) {
        return undefined;
    }
    return decode(it.value);
});
/**
 * Retrieves all bigInt {fields}.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useBigInts = makeParsedField((_) => (quad) => {
    const it = quad[QuadPosition.object];
    if (!isLiteral(it) || !bigIntTypes.some((type) => equals(it.datatype, type))) {
        return undefined;
    }
    return BigInt(it.value);
});
/**
 * Retrieves all boolean {fields}.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useBooleans = makeParsedField((_) => (quad) => {
    const it = quad[QuadPosition.object];
    if (!isLiteral(it) || !equals(it.datatype, xsdboolean)) {
        return undefined;
    }
    return it.value === "true" || it.value === "1" || it.value === "t";
});
/**
 * Retrieves all date {fields}.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useDates = makeParsedField((_) => (quad) => {
    const it = quad[QuadPosition.object];
    if (!isLiteral(it) || !(equals(it.datatype, dateTime) || equals(it.datatype, date))) {
        return undefined;
    }
    return new Date(it.value);
});
/**
 * Retrieves all langString and string {fields}.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useAnyStrings = makeParsedField((_) => (quad) => {
    const it = quad[QuadPosition.object];
    if (!isLiteral(it) || !(equals(it.datatype, langString) || equals(it.datatype, string))) {
        return undefined;
    }
    return [it.value, it.language];
});
/**
 * Retrieves all langString {fields}.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useLangStrings = makeParsedField((_) => (quad) => {
    const it = quad[QuadPosition.object];
    if (!isLiteral(it) || !equals(it.datatype, langString)) {
        return undefined;
    }
    return [it.value, it.language];
});
/**
 * Retrieves all string {fields}.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useRegularStrings = makeParsedField((_) => (quad) => {
    const it = quad[QuadPosition.object];
    if (!isLiteral(it) || !equals(it.datatype, string)) {
        return undefined;
    }
    return it.value;
});
/**
 * Retrieves all string and lang string {fields}.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useStrings = makeParsedField((_) => (quad) => {
    const it = quad[QuadPosition.object];
    if (!isLiteral(it) || !(equals(it.datatype, langString) || equals(it.datatype, string))) {
        return undefined;
    }
    return it.value;
});
/**
 * Retrieves all number {fields}.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useNumbers = makeParsedField((lrs) => (quad) => {
    const it = quad[QuadPosition.object];
    if (!isLiteral(it) || !numberTypes.some((type) => equals(it.datatype, type))) {
        return undefined;
    }
    const n = Number(it.value);
    if (isNaN(n)) {
        lrs.report(new Error(`useNumber value was NaN: '${it.value}' on ${quad}`));
        return undefined;
    }
    return n;
});
/**
 * Retrieves all url {fields}.
 * @param resource {Node} - The resource to look up. Defaults to the context subject.
 */
const useUrls = makeParsedField((lrs) => (quad) => {
    const it = quad[QuadPosition.object];
    if (!isNamedNode(it)) {
        return undefined;
    }
    try {
        return new URL(it.value);
    }
    catch (e) {
        lrs.report(e);
        return undefined;
    }
});

function useActionById(action, defaultArgs) {
    const lrs = useLRS();
    const update = useDataInvalidation(isNamedNode(action) ? action : undefined);
    const callback = React.useCallback((args) => {
        if (isNamedNode(action)) {
            return lrs.exec(action, args !== null && args !== void 0 ? args : defaultArgs);
        }
        throw new Error(`No valid action given: ${action}`);
    }, [lrs, action, update, defaultArgs]);
    return isNamedNode(action) ? callback : undefined;
}

/**
 * Keeps track of zero or more subjects with fallback to the context subject if nothing is given.
 * @param subjects The resources to invalidate on.
 * @return The subjects which have been invalidated on, and the last update time of all resources.
 */
const useSubject = (subjects) => {
    const { subject: subCtx } = useLinkRenderContext();
    const getSubjects = () => (subjects !== undefined ? subjects : subCtx);
    const targetsRef = React.useRef([]);
    const targets = React.useMemo(() => {
        const next = getSubjects();
        const changed = calculatedValueChanged(targetsRef.current, next);
        if (changed) {
            targetsRef.current = next;
            return next;
        }
        return targetsRef.current;
    }, [subjects, subCtx]);
    const lastUpdate = useDataFetching(targets);
    return [
        targets,
        lastUpdate,
    ];
};

const calculator = (lrs, targets, path, match) => {
    if (targets === undefined) {
        return NESTED_EMPTY_ARRAY;
    }
    else if (Array.isArray(targets)) {
        return [targets.map((s) => lrs.findSubject(s, path, match)), EMPTY_ARRAY];
    }
    return [lrs.findSubject(targets, path, match), EMPTY_ARRAY];
};
/**
 * Retrieve the subjects that have {match} at the ends of the {path}, starting at {subjects} or the context subject.
 *
 * @param path - Array of properties to traverse, following each hop the next property is looked up and followed.
 * @param match - The value to match the value at the end of each path against.
 * @param subjects - The resource(s) to start at. The context subject is used if omitted.
 * @return Array of subjects which match the given {match} at the end of the {path}, if the path couldn't be completed
 *   or no value was found at the end the element will be undefined.
 */
function useFindSubject(path, match, subjects) {
    const [targets, inv] = useSubject(subjects);
    return useCalculatedValue(calculator, [inv], targets, path, match);
}

function useResourceLinks(subjects, mapDataToProps, opts) {
    const defaultedOpts = opts !== null && opts !== void 0 ? opts : globalLinkOptsDefaults;
    const lrs = useLRS();
    const dataSubjects = normalizeType(subjects);
    const lastUpdate = useDataInvalidation(dataSubjects);
    const [propMap, requestedProperties] = React.useMemo(() => dataPropsToPropMap(mapDataToProps, defaultedOpts), [mapDataToProps, opts]);
    const propSets = React.useMemo(() => dataSubjects.map((subject) => {
        if (!subject) {
            return [subject, undefined];
        }
        const record = lrs.tryRecord(subject);
        return [subject.value, record !== null && record !== void 0 ? record : { _id: subject }];
    }), [
        lrs,
        useMemoizedDataSubjects(dataSubjects),
        requestedProperties,
        lastUpdate,
    ]);
    return useManyLinkedObjectProperties(propSets, propMap, defaultedOpts.returnType);
}

function useLink(dataToProps, opts) {
    const { subject } = useLinkRenderContext();
    const [data] = useResourceLinks(subject, dataToProps, opts || defaultLinkOptions);
    return data;
}

function useResourceLink(subject, dataToProps, opts) {
    const [data] = useResourceLinks(subject, dataToProps, opts !== null && opts !== void 0 ? opts : defaultLinkOptions);
    return data;
}

const arrayReturnTypes = [
    ReturnType.AllTerms,
    ReturnType.AllValues,
    ReturnType.AllLiterals,
];
const emptyOutput = (opts) => {
    return (arrayReturnTypes.includes(opts.returnType) ? [] : undefined);
};
const calculate$2 = (lrs, subject, property, opts) => {
    if (!subject) {
        return emptyOutput(opts);
    }
    const props = lrs.getResourcePropertyRaw(subject, property);
    return toReturnType(opts.returnType, props);
};
function useResourceProperty(subject, property, opts) {
    const optsOrDefault = opts || defaultPropertyOptions;
    const lrs = useLRS();
    const properties = property ? normalizeType(property) : [];
    const lastUpdate = useDataInvalidation([subject, ...properties]);
    const [value, setValue,] = React.useState(() => calculate$2(lrs, subject, properties, optsOrDefault));
    React.useEffect(() => {
        const returnValue = calculate$2(lrs, subject, properties, optsOrDefault);
        if (calculatedValueChanged(value, returnValue)) {
            setValue(returnValue);
        }
    }, [
        lrs,
        subject ? id(subject) : undefined,
        properties
            .map((p) => id(p))
            .reduce((acc, next) => acc + next, 0),
        lastUpdate,
    ]);
    return value;
}

function useProperty(property, opts) {
    const optsOrDefault = opts || defaultPropertyOptions;
    const { subject } = useLinkRenderContext();
    return useResourceProperty(subject, property, optsOrDefault);
}

function isBackgroundLoading(status) {
    const { current, previous } = status;
    return current !== RecordState.Present && (previous === RecordState.Present && current !== RecordState.Absent);
}
function isFullyLoaded(status) {
    return status.current === RecordState.Present;
}
const useRecordStatus = (id = undefined) => {
    const lrs = useLRS();
    const { subject } = useLinkRenderContext();
    const target = id !== null && id !== void 0 ? id : subject;
    const [status, setStatus] = React.useState(() => lrs.getState(target.value));
    const update = useDataInvalidation(target);
    React.useEffect(() => {
        setStatus(lrs.getState(target.value));
    }, [target, update]);
    return status;
};

const calculator$1 = (lrs, targets) => {
    if (Array.isArray(targets)) {
        return [
            targets.map((t) => isNamedNode(t) ? lrs.getStatus(t) : undefined),
            EMPTY_ARRAY,
        ];
    }
    if (!isNamedNode(targets)) {
        return [undefined, EMPTY_ARRAY];
    }
    return [lrs.getStatus(targets), EMPTY_ARRAY];
};
/**
 *  Retrieve the fetching status of {subject}. Will return the status of the context subject if no subject was passed.
 *
 *  Note that it is the request status, so blank nodes have no status.
 *
 *  @param subjects - The subject(s) to get the status for. Uses the context subject if omitted or undefined.
 *  @return A list of statuses. Cardinality with {subjects} is kept,
 *    where the first item matches {subjects} if that is not an array.
 */
function useStatus(subjects) {
    const [targets, inv] = useSubject(subjects);
    return useCalculatedValue(calculator$1, [inv], targets);
}

const updatedReference = (originalIds, newIds) => (term) => {
    if (isLiteral(term)) {
        return term;
    }
    const i = originalIds.indexOf(term);
    if (i >= 0) {
        return newIds[i];
    }
    return term;
};
const cloneRecords = (lrs, ids) => () => {
    const cloneIds = ids.map(() => rdf.blankNode());
    const store = lrs.store.getInternalStore().store;
    const replaceReference = updatedReference(ids, cloneIds);
    cloneIds.forEach((_, i) => {
        cloneRecord(lrs, ids, cloneIds, i, replaceReference);
    });
    const cleanupClones = () => {
        cloneIds.forEach((id) => store.deleteRecord(id.value));
    };
    return [cloneIds, cleanupClones];
};
const filterIncomplete = (lrs, ids) => ids.filter((id) => lrs.getState(id.value).current !== RecordState.Present);
/**
 * Makes temporary copies of the given [ids] with internal references updated.
 * Will clear the resource when the component is unmounted.
 * @return List of cloned ids, keeps ordinality.
 */
const useTempClones = (ids) => {
    const lrs = useLRS();
    const isMountedRef = React.useRef(false);
    const idCheck = JSON.stringify(ids);
    const [incomplete, setIncomplete] = React.useState(filterIncomplete(lrs, ids));
    const [[cloneIds, cleanup], setCloneState] = React.useState(cloneRecords(lrs, ids));
    React.useEffect(() => {
        if (isMountedRef.current) {
            setCloneState(cloneRecords(lrs, ids));
        }
        else {
            isMountedRef.current = true;
        }
        return cleanup;
    }, [lrs, idCheck]);
    const updated = useDataInvalidation(incomplete);
    React.useEffect(() => {
        setIncomplete(Array.from(new Set([...incomplete, ...filterIncomplete(lrs, ids)])));
    }, [idCheck]);
    React.useEffect(() => {
        const completed = incomplete
            .filter((id) => lrs.getState(id.value).current === RecordState.Present);
        if (completed.length > 0) {
            const replaceReference = updatedReference(ids, cloneIds);
            for (const id of completed) {
                cloneRecord(lrs, ids, cloneIds, ids.indexOf(id), replaceReference);
            }
            setIncomplete((prev) => filterIncomplete(lrs, prev));
        }
    }, [lrs, idCheck, updated]);
    return cloneIds;
};
const cloneRecord = (lrs, ids, cloneIds, i, replaceReference) => {
    const store = lrs.store.getInternalStore().store;
    const id = ids[i];
    const cloneId = cloneIds[i];
    const current = store.getRecord(id.value);
    const next = {
        [ll$1.clonedFrom.value]: id,
    };
    if (current) {
        const { _id: oldId, ...fields } = current;
        const updatedFields = Object.entries(fields).reduce((acc, [field, value]) => {
            return ({
                ...acc,
                [field]: Array.isArray(value) ? value.map(replaceReference) : replaceReference(value),
            });
        }, next);
        store.setRecord(cloneId.value, updatedFields);
    }
    else {
        store.setRecord(cloneId.value, next);
    }
};

const append = (props, field, value) => {
    if (value) {
        props[field.value] = typeof value === "string" ? rdf.literal(value) : value;
    }
};
/**
 * Derives a set of temporary records from the passed {items} and returns their ids.
 * Will clear the resources when the component is unmounted.
 *
 * Note that when passing an existing ids as {givenIds}, the original data will be cleared as well.
 * The arity of {items} and {givenIds} must match when {givenIds} is provided.
 *
 * @param items The list used to create the records from, will be passed individually to the {@link ItemFieldBuilder}.
 */
const useTempRecords = (items, fields, deps, givenIds) => {
    const lrs = useLRS();
    const [ids, _] = React.useState(() => givenIds !== null && givenIds !== void 0 ? givenIds : items.map(() => rdf.blankNode()));
    const setRecord = React.useCallback(() => {
        const store = lrs.store.getInternalStore().store;
        const fieldSets = items.map((item) => {
            const set = {};
            if (typeof fields === "function") {
                const bound = (field, value) => append(set, field, value);
                fields(item, bound);
                return set;
            }
            else {
                return {
                    ...fields,
                    ...set,
                };
            }
        });
        for (let i = 0, max = ids.length; i < max; i++) {
            store.setRecord(ids[i].value, fieldSets[i]);
        }
        return () => {
            for (const id of ids) {
                store.deleteRecord(id.value);
            }
        };
    }, [lrs, ids, ...deps]);
    // First render
    React.useState(setRecord);
    React.useLayoutEffect(setRecord, [setRecord]);
    return ids;
};

/**
 * Writes a temporary record into the store and returns its id.
 * Will clear the resource when the component is unmounted.
 *
 * Note that when passing an existing id as {givenId}, the original data will be cleared as well.
 */
const useTempRecord = (type$1, fields, deps, givenId) => {
    let enhancedFields;
    if (typeof fields === "function") {
        enhancedFields = (__, set) => {
            set(type, type$1);
            fields(set);
        };
    }
    else {
        enhancedFields = {
            [type.value]: type$1,
            ...fields,
        };
    }
    const memoizedId = React.useMemo(() => givenId ? [givenId] : undefined, [givenId]);
    return useTempRecords([undefined], enhancedFields, deps, memoizedId)[0];
};

/** Get the current topology */
function useTopology() {
    return React.useContext(LinkRenderCtx).topology;
}

/**
 * @unstable
 */
function useView(type, predicate = RENDER_CLASS_NAME, topology) {
    const lrs = useLRS();
    const context = useLinkRenderContext();
    return React.useMemo(() => {
        var _a;
        return (_a = lrs.getComponentForProperty(type, predicate, topology || context.topology)) !== null && _a !== void 0 ? _a : undefined;
    }, [lrs, type, predicate, topology || context.topology]);
}

const toDisplayName = (topology) => {
    var _a, _b;
    const name = ((_b = (_a = topology.value.split(/[\/#]/).pop()) === null || _a === void 0 ? void 0 : _a.split("?")) === null || _b === void 0 ? void 0 : _b.shift()) || topology.value;
    return `TP(${name[0].toUpperCase()}${name.slice(1)})`;
};
/**
 * Basic building block for making a topology.
 * @param topology The topology Iri.
 * @returns The topology provider component.
 */
const createTopologyProvider = (topology) => {
    const Provider = ({ children }) => {
        const prevContext = React.useContext(LinkRenderCtx);
        const newContext = React.useMemo(() => ({
            ...prevContext,
            topology,
        }), [topology, prevContext]);
        return (React.createElement(LinkRenderCtx.Provider, { value: newContext }, children));
    };
    Provider.displayName = toDisplayName(topology);
    return Provider;
};

const extendsLinkOpts = (predicate) => !(isNode(predicate) || Array.isArray(predicate));
const mergedMap = (predicate) => {
    const t = extendsLinkOpts(predicate)
        ? predicate
        : { label: predicate };
    return t;
};
/*                                  Arity                                 */
/** Return all props from the set. */
const all = (predicate) => ({
    ...mergedMap(predicate),
    limit: Infinity,
});
/** Return a limited number of props from the set. */
const some = (predicate, limit = 10) => ({
    ...mergedMap(predicate),
    limit,
});
/** Return a single prop from the set. */
const single = (predicate) => ({
    ...mergedMap(predicate),
    limit: 1,
});
/*                              Return type                               */
/** Sets the return type to `ReturnType.Term`. */
const term = (predicate) => ({
    ...mergedMap(predicate),
    returnType: ReturnType.Term,
});
/** Sets the return type to `ReturnType.Literal`. */
const literal = (predicate) => ({
    ...mergedMap(predicate),
    returnType: ReturnType.Literal,
});
/** Sets the return type to `ReturnType.Value`. */
const value = (predicate) => ({
    ...mergedMap(predicate),
    returnType: ReturnType.Value,
});
/** Sets the return type to `ReturnType.AllTerms`. */
const terms = (predicate) => ({
    ...mergedMap(predicate),
    returnType: ReturnType.AllTerms,
});
/** Sets the return type to `ReturnType.AllLiterals`. */
const literals = (predicate) => ({
    ...mergedMap(predicate),
    returnType: ReturnType.AllLiterals,
});
/** Sets the return type to `ReturnType.AllValues`. */
const values = (predicate) => ({
    ...mergedMap(predicate),
    returnType: ReturnType.AllValues,
});
/*                              Data fetching                             */
/**
 * Tell link to fetch the data if not present or stale.
 *
 * @see {hold} For opposite behaviour
 */
const dereference = (predicate) => ({
    ...mergedMap(predicate),
    fetch: true,
});
/**
 * Tell link to hold current data even if unfetched or stale.
 *
 * @see {hold} For opposite behaviour
 */
const hold = (predicate) => ({
    ...mergedMap(predicate),
    fetch: false,
});
/*                                Rendering                               */
/**
 * Render the component even when _no data_ could be mapped.
 */
const renderAlways = (predicate) => ({
    ...mergedMap(predicate),
    forceRender: true,
});
/**
 * Only render the component when _some_ data could be mapped.
 */
const renderPartial = (predicate) => ({
    ...mergedMap(predicate),
    forceRender: false,
});

function registerExotic(comp, type, property, topology) {
    comp.type = type;
    comp.property = property;
    comp.topology = topology;
    return register(comp);
}
function register(comp) {
    // @ts-ignore
    const wrappedComp = (comp.hocs || []).reduce((prev, cur) => cur(prev), comp);
    const dataBoundComp = link(comp.mapDataToProps, comp.linkOpts)(wrappedComp);
    return LinkedRenderStore.registerRenderer(dataBoundComp, comp.type, comp.property, comp.topology);
}

const BlankNode = PropTypes.shape({
    termType: PropTypes.oneOf(["BlankNode"]),
    value: PropTypes.string,
});
const NamedNode = PropTypes.shape({
    termType: PropTypes.oneOf(["NamedNode"]),
    value: PropTypes.string,
});
const Literal = PropTypes.shape({
    datatype: NamedNode,
    language: PropTypes.string,
    termType: PropTypes.oneOf(["Literal"]),
    value: PropTypes.string,
});
const Statement = PropTypes.shape({
    subject: PropTypes.oneOfType([BlankNode, NamedNode]),
    predicate: PropTypes.oneOfType([BlankNode, NamedNode]),
    object: PropTypes.oneOfType([BlankNode, NamedNode, Literal]),
    graph: PropTypes.oneOfType([BlankNode, NamedNode]),
});
const namedOrBlankNode = PropTypes.oneOfType([
    NamedNode,
    BlankNode,
]);
const linkType = PropTypes.oneOfType([
    NamedNode,
    BlankNode,
    Literal,
    Statement,
    PropTypes.string,
    PropTypes.arrayOf(NamedNode),
    PropTypes.arrayOf(BlankNode),
    PropTypes.arrayOf(Literal),
    PropTypes.arrayOf(Statement),
    PropTypes.arrayOf(PropTypes.string),
]);
const labelType = PropTypes.oneOfType([
    NamedNode,
    PropTypes.arrayOf(NamedNode),
]);
const linkedPropType = PropTypes.oneOfType([
    NamedNode,
    BlankNode,
    Literal,
]);
const lrsType = PropTypes.instanceOf(LinkedRenderStore);
const subjectType = namedOrBlankNode;
const topologyType = namedOrBlankNode;

export { Component, LRSCtx, LinkRenderCtx, Property, QueryType, RenderStoreProvider, Resource, ReturnType, TopologyProvider, Type, all, array, createTopologyProvider, defaultLinkOptions, defaultPropertyOptions, dereference, dig, errorComponent, except, getLinkedObjectClass, hold, isBackgroundLoading, isFullyLoaded, labelType, link, linkType, linkedPropType, literal, literals, loadingComponent, lrsType, makeParsedField, register, registerExotic, renderAlways, renderError, renderPartial, single, some, subjectType, term, terms, topologyType, useActionById, useAnyStrings, useBase64s, useBigInts, useBooleans, useCalculateChildProps, useDataFetching, useDataInvalidation, useDates, useFields, useFindSubject, useGlobalIds, useIds, useLRS, useLangStrings, useLink, useLinkRenderContext, useLiteralValues, useLiterals, useLocalIds, useNumbers, useProperty, useQuadruples, useRecordStatus, useRegularStrings, useResourceLink, useResourceLinks, useResourceProperty, useResourcePropertyView, useResourceView, useStatus, useStrings, useSubject, useTempClones, useTempRecord, useTempRecords, useTopology, useUrls, useValues, useView, value, values, withLRS, withTopology };
//# sourceMappingURL=index.js.map
